import { useCallback, useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Card } from "@/components/ranger/PageHeader";
import { api } from "@/lib/api";

const RANGES = [[6, "6h"], [24, "24h"], [72, "3d"], [168, "7d"]];
const CHARTS = [["fps", "Server FPS", "#E5202E"], ["players", "Players online", "#38BDF8"], ["entities", "Entities", "#A3E635"], ["memory", "Memory (MB)", "#F59E0B"]];
const tick = (v) => new Date(v).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const Stat = ({ label, value, testId }) => (
  <div className="rounded-sm border border-[#1E2D4A] px-3 py-2"><div className="rg-label">{label}</div><div data-testid={testId} className="font-mono text-lg text-slate-100">{value ?? "—"}</div></div>
);

const Chart = ({ data, k, label, color }) => (
  <div data-testid={`perf-chart-${k}`} className="rounded-sm border border-[#1E2D4A] p-3">
    <div className="rg-label mb-2">{label}</div>
    <ResponsiveContainer width="100%" height={150}>
      <AreaChart data={data} margin={{ left: 0, right: 4, top: 4, bottom: 0 }}>
        <defs><linearGradient id={`g-${k}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.35} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
        <CartesianGrid stroke="#1E2D4A" strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="at" tickFormatter={tick} stroke="#475569" fontSize={10} minTickGap={40} />
        <YAxis stroke="#475569" fontSize={10} width={44} allowDecimals={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
        <Tooltip contentStyle={{ background: "#0B1120", border: "1px solid #1E2D4A", fontSize: 12 }} labelFormatter={(v) => new Date(v).toLocaleString()} />
        <Area type="monotone" dataKey={k} stroke={color} fill={`url(#g-${k})`} strokeWidth={1.5} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

export const PerfGraphs = ({ cid }) => {
  const [hours, setHours] = useState(24);
  const [d, setD] = useState(null);
  const load = useCallback(() => api.get(`/communities/${cid}/perf?hours=${hours}`).then((r) => setD(r.data)).catch(() => setD({ points: [], summary: {} })), [cid, hours]);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const s = d?.summary || {};
  const range = (
    <div className="flex gap-1">{RANGES.map(([h, l]) => (
      <button key={h} data-testid={`perf-range-${l}`} onClick={() => setHours(h)} className={`rounded-sm px-2 py-1 font-mono text-xs transition-colors ${hours === h ? "bg-[#E5202E]/20 text-[#E5202E]" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
    ))}</div>
  );
  return (
    <Card title="Performance" icon={Activity} testId="perf-card" right={range} className="mb-5">
      <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="FPS now" value={s.latest?.fps} testId="perf-fps-now" />
        <Stat label="Avg FPS" value={s.avg_fps} testId="perf-fps-avg" />
        <Stat label="Lowest FPS" value={s.min_fps} testId="perf-fps-min" />
        <Stat label="Peak players" value={s.peak_players} testId="perf-peak-players" />
      </div>
      {!d ? null : d.points.length === 0 ? <p data-testid="perf-empty" className="text-sm text-slate-500">No samples yet. RANGER records FPS, players, entities and memory from serverinfo every minute while RCON is connected.</p> : (
        <div className="grid gap-3 lg:grid-cols-2">{CHARTS.map(([k, l, c]) => <Chart key={k} data={d.points} k={k} label={l} color={c} />)}</div>
      )}
    </Card>
  );
};
